import React, { useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import i18n from '../i18n'
import Icon from '../components/icons'
import Text from '../elements/Typo/Text'
import { formatNumberWithCommas, getColor, getImpactValue, trackEvent } from './functions'
import InconclusiveReasonModal from '../components/modal/InconclusiveReason.modal'
import DropDownFilter from '../components/gridCustomInput/DropDownFilter'
import moment from 'moment'
import ColumnHeader from '../components/Table/columnHeader'
import InsightsDialog from '../pages/MyExperiment/components/topLearnings/components/insightsDialog'
import { FaCheck } from 'react-icons/fa6'

type filterColumnType = 'name' | 'hypothesis' | 'use_case' | 'status' | 'owner' | 'kpi' | 'country' | 'experiment_literal'

export const experimentColumnFilter = (column: filterColumnType, filterPayload: any, onApplyFilter: (value: any) => void) => {
    let filterKeys = {}
    switch (column) {
        case 'use_case':
            filterKeys = { columnKey: 'use_case', filterKey: 'use_case_ids' }
            break
        case 'status':
            filterKeys = { columnKey: 'executive_dashboard_status', filterKey: 'executive_dashboard_status' }
            break
        case 'owner':
            filterKeys = { columnKey: 'experiment_owner', filterKey: 'created_by_ids' }
            break
        case 'kpi':
            filterKeys = { columnKey: 'default_kpi', filterKey: 'default_kpi' }
            break
        case 'hypothesis':
            filterKeys = { columnKey: 'hypothesis', filterKey: 'hypothesis' }
            break
        case 'name':
            filterKeys = { columnKey: 'experiment_name', filterKey: 'experiment_name' }
            break

        case 'experiment_literal':
            filterKeys = { columnKey: 'experiment_literal', filterKey: 'experiment_literal' }
            break
        case 'country':
            filterKeys = { columnKey: 'country', filterKey: 'country_ids' }
            break
        default:
            filterKeys = {}
    }

    return {
        filter: DropDownFilter,
        filterParams: {
            ...filterKeys,
            multi: true,
            isClearable: true,
            selectedFilters: filterPayload,
            onApplyFilter,
        },
    }
}

export const onSortChanged = (sortModel: any) => {
    //country, business_function, use_case, experiment_type, created_by, default_kpi
    let col = '-created_at'
    if (sortModel) {
        const normalizedCol = (() => {
            const rawCol = sortModel.col
            const baseCols = ['Country', 'Region', 'Business Function', 'Use Case', 'Expt. Type', 'Created By', 'Expt. Owner', 'KPI', 'Expt. Status', 'Name']
            const matchedBaseCol = baseCols.find((baseCol) => rawCol === i18n.t(baseCol))
            return matchedBaseCol || rawCol
        })()

        switch (normalizedCol) {
            case 'Country':
            case 'Region':
                col = 'country'
                break
            case 'Business Function':
                col = 'business_function'
                break
            case 'Use Case':
                col = 'use_case'
                break
            case 'experiment_type':
            case 'Expt. Type':
                col = 'experiment_type'
                break
            case 'Created By':
            case 'Expt. Owner':
                col = 'created_by'
                break
            case 'kpi':
            case 'KPI':
                col = 'default_kpi'
                break
            case 'Expt. Status':
                col = 'executive_dashboard_status'
                break
            case 'Name':
                col = 'experiment_name'
                break

            default:
                col = sortModel.col.toLowerCase()
                break
        }
        if (sortModel.sort == 'desc') {
            col = '-' + col
        }
    }

    return col
}

interface ExperimentStatusProps {
    params: any
}
const ExperimentStatus: React.FC<ExperimentStatusProps> = ({ params }) => {
    const [InconclusiveLiteral, setInconclusiveLiteral] = React.useState<string | null>(null)

    return (
        <div className="flex flex-col items-center justify-center h-full" title={params.data?.executive_dashboard_status}>
            <div className="flex items-center gap-1  w-full ">
                <div className="size-[0.41rem] rounded-full" style={{ backgroundColor: getColor(params.data?.status_text), opacity: 0.6 }}>
                    {params.data?.executive_dashboard_status !== 'In Progress' && <FaCheck className="size-[0.41rem] text-white" />}
                </div>
                {params.data?.status == 401 ? (
                    <Text
                        size="sm"
                        className="underline italic cursor-pointer items-center font-normal text-[##0000EE] truncate"
                        onClick={() => {
                            setInconclusiveLiteral(params.data.experiment_literal)
                        }}
                    >
                        {params.data?.executive_dashboard_status}
                    </Text>
                ) : (
                    <Text size="sm" className="font-normal text-[#464E5F] truncate">
                        {params.data?.executive_dashboard_status}
                    </Text>
                )}
            </div>
            {InconclusiveLiteral && createPortal(<InconclusiveReasonModal literal={InconclusiveLiteral} isModalOpen={!!InconclusiveLiteral} onClose={() => setInconclusiveLiteral(null)} />, document.body)}
        </div>
    )
}

export const getUpliftValue = (x: number) => {
    if (x == 0) {
        return '0%'
    } else if (x) {
        return formatNumberWithCommas(+x.toFixed(2)) + '%'
    } else {
        return '-'
    }
}

export const getValueColor = (x: number) => {
    if (x == 0) {
        return 'text-[#DBC848]'
    } else if (x > 0) {
        return 'text-[#28A526]'
    } else {
        return 'text-[#F23F44]'
    }
}

export const CellStyle = {
    paddingLeft: 5,
    paddingRight: 2,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
}

export const experimentNameColumn = {
    get headerName() {
        return i18n.t('Expt. Name')
    },
    field: 'experiment_name',
    sortable: false,
    suppressHeaderMenuButton: true,
    flex: 1,
    minWidth: 200,
    cellStyle: CellStyle,
    headerComponent: ColumnHeader,
    cellRenderer: (params: any) => {
        return (
            <div className="flex items-center gap-1 w-full pr-1" style={{ color: '#C89F1B' }}>
                {params.data.parent_experiment_id && <Icon name="repeat" size={'0.813'} title={i18n.t('Recurring Experient')} />}
                {params.data.is_partial_measurement && <Icon name="timer" size={'0.813'} title={i18n.t('Recurring Experient')} />}
                <Text className="!text-[0.566rem] font-medium truncate" title={params.data.experiment_name ?? '-'}>
                    {params.data.experiment_name ?? '-'}
                </Text>
            </div>
        )
    },
}

export const experimentIDColumn = {
    get headerName() {
        return i18n.t('Expt. ID')
    },
    field: 'experiment_name',
    filter: false,
    sortable: false,
    suppressHeaderMenuButton: true,
    width: 200,
    cellStyle: CellStyle,
    headerComponent: ColumnHeader,
    cellRenderer: (params: any) => {
        return (
            <div className="flex items-center gap-1 w-full pr-1" style={{ color: '#C89F1B' }}>
                <Text className="!text-[0.566rem] font-medium truncate" title={params.data.experiment_name ?? '-'}>
                    {params.data.experiment_name ?? '-'}
                </Text>
            </div>
        )
    },
}
export const experimentTypeColumn = {
    get headerName() {
        return i18n.t('Expt. Type')
    },
    field: 'experiment_type.title',
    sortable: false,
    filter: false,
    suppressHeaderMenuButton: true,
    width: 100,
    cellStyle: CellStyle,
    headerComponent: ColumnHeader,
    cellRenderer: (params: any) => {
        return (
            <Text className="!text-[0.458rem] font-medium truncate" title={params.data.experiment_type?.title || '-'}>
                {params.data.experiment_type?.title || '-'}
            </Text>
        )
    },
}

export const hypothesisColumn = {
    get headerName() {
        return i18n.t('Hypothesis')
    },
    field: 'hypothesis',
    filter: false,
    headerComponent: ColumnHeader,
    suppressHeaderMenuButton: true,
    sortable: false,
    headerClass: 'summery-experiment-header',
    cellRenderer: (params: any) => {
        return (
            <div className="flex flex-col justify-center w-full leading-3 h-full">
                <Text size="xs" className="font-medium truncate" title={params.data.hypothesis ?? '-'}>
                    {params.data.hypothesis ?? '-'}
                </Text>
                {}
                <Text className="!text-[0.5rem] font-[#6E6E71]" title={`${params.data.start_date ? moment(params.data.start_date).format('MM-DD-YYYY') : ''} - ${params.data.end_date ? moment(params.data.end_date).format('MM-DD-YYYY') : ''}`}>
                    {params.data.start_date ? moment(params.data.start_date).format('MM-DD-YYYY') : ''} - {params.data.end_date ? moment(params.data.end_date).format('MM-DD-YYYY') : ''}
                </Text>
            </div>
        )
    },
}

export const experimentOwnerColumn = {
    get headerName() {
        return i18n.t('Expt. Owner')
    },
    field: 'user.first_name',
    filter: false,
    sortable: false,
    suppressSorting: true,
    headerComponent: ColumnHeader,
    suppressHeaderMenuButton: true,
    width: 150,
    cellStyle: { ...CellStyle, paddingRight: 5 },
    cellRenderer: (params: any) => {
        return (
            <div className="flex flex-col items-center justify-center h-full" title={`${params.data?.user.first_name} ${params.data?.user.last_name}`}>
                <div className="flex items-center gap-1  w-full ">
                    <Text size="sm" className="font-normal text-[#464E5F] truncate w-full ">
                        {params.data?.user.first_name} {params.data?.user.last_name}
                    </Text>
                </div>
            </div>
        )
    },
}
export const countryColumn = {
    get headerName() {
        return i18n.t('Country')
    },
    headerComponent: ColumnHeader,
    field: 'country.title',
    width: 100,
    filter: false,
    sortable: false,
    cellRenderer: (params: any) => <div className="flex items-center justify-center text-[0.542rem] font-medium h-full w-full">{params?.data?.country?.title ?? '-'}</div>,
}

export const useCaseColumn = {
    get headerName() {
        return i18n.t('Use Case')
    },
    field: 'use_case.title',
    filter: false,
    suppressHeaderMenuButton: true,
    sortable: false,
    width: 120,
    cellStyle: CellStyle,
    headerComponent: ColumnHeader,
    cellRenderer: (params: any) => {
        return (
            <Text size="xs" className="w-full font-semibold text-base-content/60 truncate" title={params.data?.use_case?.title ?? '-'}>
                {params.data?.use_case?.title ?? '-'}
            </Text>
        )
    },
}

export const kpiColumn = {
    get headerName() {
        return i18n.t('KPI')
    },
    field: 'experiment_metadatas.metrices[0].literral_value',
    filter: false,
    suppressHeaderMenuButton: true,
    sortable: false,
    width: 100,
    cellStyle: CellStyle,
    headerComponent: ColumnHeader,
    cellRenderer: (params: any) => {
        return (
            <Text size="xs" className="font-semibold items-center truncate" title={params.data?.experiment_metadatas?.metrices?.[0]?.literral_value ?? '-'}>
                {params.data?.experiment_metadatas?.metrices?.[0]?.literral_value ?? '-'}
            </Text>
        )
    },
}

export const experimentStatusColumn = {
    get headerName() {
        return i18n.t('Expt. Status')
    },
    field: 'executive_dashboard_status',
    filter: false,
    sortable: false,
    suppressHeaderMenuButton: true,
    width: 120,
    cellStyle: { ...CellStyle, paddingRight: 5 },
    headerComponent: ColumnHeader,
    cellRenderer: (params: any) => {
        return <ExperimentStatus params={params} />
    },
}

export const impactColumn = {
    get headerName() {
        return i18n.t('Impact')
    },
    field: 'impact',
    filter: false,
    sortable: true,
    suppressSorting: true,
    headerComponent: ColumnHeader,
    width: 100,
    suppressHeaderMenuButton: true,
    cellStyle: CellStyle,
    cellRenderer: (params: any) => {
        const kpiValue = params.data?.experiment_metadatas?.metrices?.[0]?.literral_value
        const kpiUnit = params.data?.experiment_metadatas?.metrices?.[0]?.kpi_unit
        const noValueStatuses = ['Failed', 'In Progress']
        let displayValue
        if (params.data.uplift === 0) {
            displayValue = '0'
        } else if (noValueStatuses.includes(params.data.executive_dashboard_status)) {
            displayValue = '-'
        } else {
            displayValue = getImpactValue(kpiValue, kpiUnit, params.value)
        }
        return (
            <div className="flex flex-col  h-full justify-center">
                <Text size="xs" className=" justify-center text-base-content/60 inline-flex items-center gap-2 font-semibold">
                    {displayValue}
                </Text>
            </div>
        )
    },
}

export const upliftColumn = {
    get headerName() {
        return i18n.t('Uplift')
    },
    field: 'uplift',
    filter: false,
    sortable: true,
    suppressSorting: true,
    width: 100,
    suppressHeaderMenuButton: true,
    headerComponent: ColumnHeader,
    cellStyle: CellStyle,
    cellRenderer: (params: any) => {
        console.log('params.value', params.value, params)
        const x = params.value
        return (
            <div className="flex flex-col h-full justify-center">
                <span className="text-[#DBC848]"></span>
                <span className="text-[#28A526]"></span>
                <span className="text-[#F23F44]"></span>
                <Text size="xs" className={`justify-center text-base-content/60 inline-flex items-center gap-2 !${getValueColor(x)} font-medium`}>
                    {getUpliftValue(x)}
                </Text>
            </div>
        )
    },
}

const InsightCell: React.FC<{ params: any }> = ({ params }) => {
    const infoButtonRef = useRef<any>(null)
    const insight = params.data.blocking_factors_formated?.join(',\n')
    const [infoDialogData, setInfoDialogData] = useState<boolean>(false)

    const toggleMenu = (button: any, item?: any, type?: 'info' | 'experiment') => {
        if (!infoDialogData) {
            trackEvent('exec_dashboard_learning_insight_dialog_opened', { experiment_literal: params.data?.experiment_literal })
        }
        setInfoDialogData(!infoDialogData)
    }

    if (!params.data.blocking_factors_formated?.length) {
        return <div className="flex items-center h-full justify-center gap-5 text-[0.542rem]  w-full">{i18n.t('NA')}</div>
    }

    return (
        <>
            <Text size="xs" className="flex-wrap text-wrap flex text-center items-center h-full justify-center" title={insight}>
                <Icon name="bulb" size={'0.85'} className="text-[#DBC848]" ref={infoButtonRef} onClick={(e: any) => toggleMenu(e.target, params.data, 'info')} />
            </Text>
            {infoDialogData && createPortal(<InsightsDialog data-testid="dialog-data" buttonRef={infoButtonRef?.current} dialogData={params.data} toggleMenu={toggleMenu} />, document.body)}
        </>
    )
}
const blockingFactorCellRenderer = (params: any) => {
    return <InsightCell params={params} />
}

export default InsightCell

export const insightColumn = {
    get headerName() {
        return i18n.t('Insight')
    },
    field: 'blocking_factors_formated',
    sortable: false,
    filter: false,
    width: 70,
    headerClass: 'table-header-center',
    cellRenderer: blockingFactorCellRenderer,
    suppressHeaderMenuButton: true,
    headerComponent: ColumnHeader,
    cellStyle: CellStyle,
}
